import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";

export type ContentStatus = "DRAFT" | "PUBLISHED" | "ARCHIVED";

export interface AcademyAdminStage {
  key: string;
  name: string;
  sub: string;
  order: number;
  status: ContentStatus;
  conceptCount: number;
  publishedConceptCount: number;
}

export interface AcademyAdminArea {
  key: string;
  name: string;
  sub: string;
  icon: string;
  order: number;
  status: ContentStatus;
  stages: AcademyAdminStage[];
}

export interface AcademyAdminConceptSummary {
  slug: string;
  name: string;
  facet: string;
  difficulty: string | null;
  status: ContentStatus;
  order: number;
  areaKey: string;
  areaName: string;
  stageKey: string;
  stageName: string;
  assetCount: number;
  questionCount: number;
}

export interface AcademyAdminAsset {
  id: number;
  kind: string;
  kindLabel: string;
  isGradeable: boolean;
  status: ContentStatus;
  order: number;
  masteryWeight: number | null;
  version: number;
  // Answer keys included — this is the admin view, and the answer key is
  // the thing most worth checking before publishing.
  payload: unknown;
  metadata: unknown;
}

export interface AcademyAdminConcept {
  slug: string;
  name: string;
  definition: string;
  learningObjectives: string[];
  facet: string;
  difficulty: string | null;
  status: ContentStatus;
  order: number;
  areaKey: string;
  areaName: string;
  stageKey: string;
  stageName: string;
  prerequisites: { slug: string; name: string; status: ContentStatus }[];
  assets: AcademyAdminAsset[];
}

export interface AcademyAdminAssetKind {
  key: string;
  label: string;
  isGradeable: boolean;
  description: string | null;
  // A working example payload for this kind, or null if none is
  // defined — in which case the editor starts from an empty object,
  // which is the old behaviour rather than a broken one.
  hint?: string | null;
  template?: Record<string, unknown> | null;
}

export interface AcademyVocabulary {
  zoneIds: string[];
  markingIds: string[];
  formations: { key: string; roles: string[] }[];
  beatVisualKinds: { kind: string; use: string }[];
}

export interface AcademyConceptFilters {
  areaKey?: string;
  status?: ContentStatus;
}

// apiClient has a response interceptor that already returns res.data, so
// these return the call directly. Destructuring { data } off it — the
// usual axios idiom — pulls .data off an ARRAY and yields undefined,
// which renders as an empty page with no error anywhere.
export interface UpdateConceptRequest {
  name?: string;
  definition?: string;
  learningObjectives?: string[];
  difficulty?: string;
  order?: number;
}

export interface CreateConceptRequest {
  slug: string;
  name: string;
  definition: string;
  learningObjectives: string[];
  facet: string;
  difficulty: string;
  areaKey: string;
  stageKey: string;
  order?: number;
}

export interface CreateAssetRequest {
  kind: string;
  payload: unknown;
  masteryWeight?: number;
  order?: number;
}

export interface AreaInput {
  key: string;
  name: string;
  sub: string;
  icon: string;
  order?: number;
}

export interface StageInput {
  key: string;
  name: string;
  sub: string;
  order?: number;
}

export const academyAdminService = {
  listAreas: (): Promise<AcademyAdminArea[]> =>
    apiClient.get(API_ENDPOINTS.ACADEMY_ADMIN.AREAS),

  listConcepts: (
    filters?: AcademyConceptFilters
  ): Promise<AcademyAdminConceptSummary[]> =>
    apiClient.get(API_ENDPOINTS.ACADEMY_ADMIN.CONCEPTS, { params: filters }),

  getConcept: (slug: string): Promise<AcademyAdminConcept> =>
    apiClient.get(API_ENDPOINTS.ACADEMY_ADMIN.CONCEPT(slug)),

  listAssetKinds: (): Promise<AcademyAdminAssetKind[]> =>
    apiClient.get(API_ENDPOINTS.ACADEMY_ADMIN.ASSET_KINDS),

  vocabulary: (): Promise<AcademyVocabulary> =>
    apiClient.get(API_ENDPOINTS.ACADEMY_ADMIN.VOCABULARY),

  updateConcept: (
    slug: string,
    data: UpdateConceptRequest
  ): Promise<AcademyAdminConcept> =>
    apiClient.patch(API_ENDPOINTS.ACADEMY_ADMIN.CONCEPT(slug), data),

  setConceptStatus: (
    slug: string,
    status: ContentStatus
  ): Promise<AcademyAdminConcept> =>
    apiClient.patch(API_ENDPOINTS.ACADEMY_ADMIN.CONCEPT_STATUS(slug), {
      status,
    }),

  updateAssetPayload: (
    assetId: number,
    payload: unknown
  ): Promise<AcademyAdminConcept> =>
    apiClient.patch(API_ENDPOINTS.ACADEMY_ADMIN.ASSET_PAYLOAD(assetId), {
      payload,
    }),

  createConcept: (data: CreateConceptRequest): Promise<AcademyAdminConcept> =>
    apiClient.post(API_ENDPOINTS.ACADEMY_ADMIN.CREATE_CONCEPT, data),

  createAsset: (
    slug: string,
    data: CreateAssetRequest
  ): Promise<AcademyAdminConcept> =>
    apiClient.post(API_ENDPOINTS.ACADEMY_ADMIN.CREATE_ASSET(slug), data),

  deleteAsset: (assetId: number): Promise<AcademyAdminConcept> =>
    apiClient.delete(API_ENDPOINTS.ACADEMY_ADMIN.ASSET(assetId)),

  setPrerequisites: (
    slug: string,
    prerequisiteSlugs: string[]
  ): Promise<AcademyAdminConcept> =>
    apiClient.patch(API_ENDPOINTS.ACADEMY_ADMIN.PREREQUISITES(slug), {
      prerequisiteSlugs,
    }),

  reorderAssets: (
    slug: string,
    assetIds: number[]
  ): Promise<AcademyAdminConcept> =>
    apiClient.patch(API_ENDPOINTS.ACADEMY_ADMIN.REORDER_ASSETS(slug), {
      assetIds,
    }),

  createArea: (data: AreaInput): Promise<AcademyAdminArea[]> =>
    apiClient.post(API_ENDPOINTS.ACADEMY_ADMIN.AREAS, data),

  updateArea: (
    key: string,
    data: Partial<Omit<AreaInput, "key">>
  ): Promise<AcademyAdminArea[]> =>
    apiClient.patch(API_ENDPOINTS.ACADEMY_ADMIN.AREA(key), data),

  setAreaStatus: (
    key: string,
    status: ContentStatus
  ): Promise<AcademyAdminArea[]> =>
    apiClient.patch(API_ENDPOINTS.ACADEMY_ADMIN.AREA_STATUS(key), { status }),

  deleteArea: (key: string): Promise<AcademyAdminArea[]> =>
    apiClient.delete(API_ENDPOINTS.ACADEMY_ADMIN.AREA(key)),

  createStage: (
    areaKey: string,
    data: StageInput
  ): Promise<AcademyAdminArea[]> =>
    apiClient.post(API_ENDPOINTS.ACADEMY_ADMIN.STAGES(areaKey), data),

  updateStage: (
    areaKey: string,
    stageKey: string,
    data: Partial<Omit<StageInput, "key">>
  ): Promise<AcademyAdminArea[]> =>
    apiClient.patch(API_ENDPOINTS.ACADEMY_ADMIN.STAGE(areaKey, stageKey), data),

  deleteStage: (
    areaKey: string,
    stageKey: string
  ): Promise<AcademyAdminArea[]> =>
    apiClient.delete(API_ENDPOINTS.ACADEMY_ADMIN.STAGE(areaKey, stageKey)),

  moveConcept: (
    slug: string,
    areaKey: string,
    stageKey: string
  ): Promise<AcademyAdminConcept> =>
    apiClient.patch(API_ENDPOINTS.ACADEMY_ADMIN.MOVE_CONCEPT(slug), {
      areaKey,
      stageKey,
    }),
};
